// Signed-out forms (LogIn, Register boards): what each field needs, the one
// line shown under the field group, and the live username check. Copy
// follows the app's auth screens (lib/screens/auth on flowstate master).

const API = process.env.VUE_APP_API_URL;

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// The invitation page's stricter rule (unchanged from the old form).
export const INVITE_USERNAME_PATTERN = /^[a-zA-Z0-9_]{3,15}$/;
export const MIN_PASSWORD = 8;

export const TERMS_REQUIRED =
  "Tick the box to agree to the Terms and Privacy Policy.";
const OFFLINE = "Can't reach cloudloop. Check your connection and try again.";

// { field, message } for the first problem, or null.
export function validateLogin({ username, password }) {
  if (!(username || "").trim())
    return { field: "username", message: "Enter your username or email." };
  if (!password) return { field: "password", message: "Enter your password." };
  return null;
}

export function validateRegister({ username, email, password }) {
  if (!(username || "").trim())
    return { field: "username", message: "Pick a username." };
  if (!/.+@.+\..+/.test(email || ""))
    return { field: "email", message: "Enter a valid email." };
  if (!password) return { field: "password", message: "Pick a password." };
  return null;
}

// The invitation's sign-up branch keeps its old rules: 3 to 15 letters,
// numbers or underscores, and 8 characters of password.
export function validateInviteSignUp({ username, password }) {
  const name = (username || "").trim();
  if (!name) return { field: "username", message: "Pick a username." };
  // The page sends the trimmed name, so test that: a trailing space from a
  // phone keyboard is not the user's mistake.
  if (!INVITE_USERNAME_PATTERN.test(name)) {
    return {
      field: "username",
      message: "Use 3 to 15 letters, numbers or underscores.",
    };
  }
  return validatePassword(password, "Pick a password.");
}

// A new password: present and at least 8 characters. Never use this to log
// in: the server never had a minimum, so older accounts have shorter ones.
export function validatePassword(password, empty = "Enter your password.") {
  if (!password) return { field: "password", message: empty };
  if (password.length < MIN_PASSWORD) {
    return {
      field: "password",
      message: `Use at least ${MIN_PASSWORD} characters.`,
    };
  }
  return null;
}

// The API helpers reject with the HTTP status (a number), or with the
// fetch error when the network is down.
export function loginErrorText(err) {
  if (typeof err !== "number") return OFFLINE;
  if (err >= 500) return "Something went wrong. Try again.";
  if (err === 429) return "Too many tries. Wait a minute and try again.";
  return "That username or password doesn't match.";
}

// A 409 from /api/register_user means the username is taken OR the email
// already has an account (the unique index on lower(email)). The live check
// catches taken names before submit, so a 409 that gets through is most
// often the email: say both, never single out the username. The invitation
// knows its email has no account, so it passes { emailIsNew: true }.
export const REGISTER_CONFLICT = "That username or email is already registered.";

export function registerErrorText(err, { emailIsNew = false } = {}) {
  if (typeof err !== "number") return OFFLINE;
  if (err === 409) return emailIsNew ? "That username is taken." : REGISTER_CONFLICT;
  if (err === 429) return "Too many tries. Wait a minute and try again.";
  return "Couldn't create your account. Try again.";
}

// Is this username free? true / false, or null when we can't tell (rate
// limited, an older server, offline): then no indicator shows. Never
// blocks Create account. GET /api/username_available, no sign-in.
export async function checkUsernameAvailable(
  username,
  fetchImpl = globalThis.fetch,
) {
  const name = (username || "").trim();
  if (!name || /\s/.test(name)) return null;
  try {
    const res = await fetchImpl(
      `${API}/api/username_available?username=${encodeURIComponent(name)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const body = await res.json();
    const r = body && body.data && body.data.results;
    return r && typeof r.available === "boolean" ? r.available : null;
  } catch (e) {
    return null;
  }
}

// Debounced checks while typing; only the latest answer for the name still
// in the field is reported. onResult(name, available | null).
export function usernameChecker(
  onResult,
  delay = 400,
  check = checkUsernameAvailable,
) {
  let timer = null;
  let seq = 0;
  return {
    update(text) {
      clearTimeout(timer);
      const name = (text || "").trim();
      const mine = ++seq;
      onResult(name, null);
      if (!name || /\s/.test(name)) return;
      timer = setTimeout(async () => {
        const answer = await check(name);
        if (mine === seq) onResult(name, answer);
      }, delay);
    },
    cancel() {
      clearTimeout(timer);
      seq++;
    },
  };
}

// '"maya" is available.' / '"maya" is taken.'
export function availabilityLine(name, available) {
  if (available == null || !name) return null;
  return available ? `“${name}” is available.` : `“${name}” is taken.`;
}

// Where the log in / create account links go, keeping ?redirect= so the
// visitor resumes what they started.
export function authLink(path, route) {
  const redirect = route && route.query && route.query.redirect;
  return typeof redirect === "string" && redirect.startsWith("/")
    ? { path, query: { redirect } }
    : { path };
}

// The account a token belongs to: the JWT's `sub` claim, or null.
export function jwtSubject(token) {
  try {
    const part = String(token).split(".")[1];
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const sub = JSON.parse(new TextDecoder().decode(bytes)).sub;
    return typeof sub === "string" && sub ? sub : null;
  } catch (e) {
    return null;
  }
}

// Log in with an email (or a name typed with other case or spaces), as the
// app does: flexible_login asks the server to match the account, and the
// canonical username comes back as the token's `sub`. Rejects like the API
// helpers, with the HTTP status or the network error.
export async function flexibleLogIn(
  identifier,
  password,
  fetchImpl = globalThis.fetch,
) {
  const res = await fetchImpl(`${API}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      username: identifier,
      password,
      flexible_login: true,
    }),
  });
  if (!res.ok) throw res.status;
  const body = await res.json();
  const token = body && body.data && body.data.results;
  const username = jwtSubject(token);
  if (!username) throw 500;
  return { token, username };
}

// Log in only (SI-14): whether the public channel has nothing to browse yet,
// which sends the user to Get started instead of Sessions: Mine. One row
// answers it, so ask for a page of one: the unpaged list is every public doc,
// and on a cold edge cache the server rebuilds each from Postgres first (a
// fresh review env took ~40 s). An answer that is slow, or fails, counts as
// "not empty", so logging in never waits on it.
//   fetchPage  () => Promise of the API envelope ({ data: { results } })
export function publicChannelIsEmpty(fetchPage, { timeoutMs = 1500 } = {}) {
  const check = Promise.resolve()
    .then(fetchPage)
    .then((body) => {
      const results = body && body.data ? body.data.results : null;
      const rows = Array.isArray(results) ? results : results && results.sessions;
      return !rows || rows.length === 0;
    })
    .catch(() => false);
  let timer;
  const late = new Promise((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  return Promise.race([check, late]).finally(() => clearTimeout(timer));
}
